import React, { useEffect, useState, useContext } from 'react';
import { Target, MessageSquare, Calendar, Award, BookOpen, Users, Check, GripVertical, Plus, X, Clock, Edit2, CheckCircle, Circle, AlertCircle, Lock, Send, ThumbsUp, ThumbsDown, Eye } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { Modal } from '../ui/Modal';
interface PerformanceProps {
  isManager?: boolean;
}
interface AgendaItem {
  id: number;
  text: string;
  duration?: number;
  owner?: string;
  status?: 'pending' | 'in-progress' | 'completed';
  notes?: string;
}
interface AnonymousComment {
  id: number;
  text: string;
  timestamp: string;
  isRead: boolean;
}
interface OneOnOne {
  id: number;
  with: string;
  date: string;
  status: 'upcoming' | 'completed';
  agenda?: AgendaItem[];
  summary?: string;
  anonymousComments?: AnonymousComment[];
}
interface FeedbackRequest {
  id: number;
  from: string;
  to: string;
  date: string;
  type: 'positive' | 'constructive';
  message: string;
  status: 'pending' | 'approved' | 'rejected';
  managerComment?: string;
}
export function Performance({
  isManager = false
}: PerformanceProps) {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'objectives');
  const [isEditingPDI, setIsEditingPDI] = useState(false);
  const [readFeedbacks, setReadFeedbacks] = useState<number[]>([]);
  const {
    showToast
  } = useToast();
  const {
    userName
  } = useContext(UserContext);
  // Modal state for agenda editing
  const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);
  const [editingOneOnOneId, setEditingOneOnOneId] = useState<number | null>(null);
  const [tempAgenda, setTempAgenda] = useState<AgendaItem[]>([]);
  const [newAgendaItem, setNewAgendaItem] = useState('');
  const [newAgendaDuration, setNewAgendaDuration] = useState(15);
  const [newAgendaOwner, setNewAgendaOwner] = useState('');
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingItemText, setEditingItemText] = useState('');
  // Modal state for anonymous comments
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [viewingCommentsOneOnOneId, setViewingCommentsOneOnOneId] = useState<number | null>(null);
  const [newAnonymousComment, setNewAnonymousComment] = useState('');
  // Modal state for sending feedback
  const [isSendFeedbackModalOpen, setIsSendFeedbackModalOpen] = useState(false);
  const [feedbackRecipient, setFeedbackRecipient] = useState('');
  const [feedbackType, setFeedbackType] = useState<'positive' | 'constructive'>('positive');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  // Modal state for reviewing feedback (manager)
  const [isReviewFeedbackModalOpen, setIsReviewFeedbackModalOpen] = useState(false);
  const [reviewingFeedback, setReviewingFeedback] = useState<FeedbackRequest | null>(null);
  const [managerComment, setManagerComment] = useState('');
  // Feedback requests state
  const [feedbackRequests, setFeedbackRequests] = useState<FeedbackRequest[]>([{
    id: 1,
    from: 'Marie Dupont',
    to: 'Thomas Petit',
    date: '2023-12-10',
    type: 'positive',
    message: 'Excellent travail sur le projet de rénovation. Ta rigueur et ton attention aux détails ont vraiment fait la différence.',
    status: 'pending'
  }, {
    id: 2,
    from: 'Sophie Bernard',
    to: 'Marie Dupont',
    date: '2023-12-08',
    type: 'constructive',
    message: "Pense à mieux communiquer les changements de planning à l'équipe pour éviter les malentendus.",
    status: 'pending'
  }, {
    id: 3,
    from: 'Thomas Petit',
    to: 'Sophie Bernard',
    date: '2023-12-05',
    type: 'positive',
    message: 'Merci pour ton aide sur la gestion des matériaux. Ton organisation est exemplaire.',
    status: 'approved'
  }]);
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);
  // Mock data - Construction themed
  const objectives = [{
    id: 1,
    title: 'Réduire les incidents de sécurité sur les chantiers',
    progress: 85,
    alignedTo: 'Objectif équipe: Sécurité et conformité',
    dueDate: '2023-12-31'
  }, {
    id: 2,
    title: 'Améliorer le respect des échéanciers de projet',
    progress: 70,
    alignedTo: 'Objectif équipe: Efficacité opérationnelle',
    dueDate: '2023-12-31'
  }, {
    id: 3,
    title: "Développer mes compétences en gestion d'équipe de chantier",
    progress: 55,
    alignedTo: 'Développement personnel',
    dueDate: '2023-12-31'
  }];
  // All approved feedbacks in the system
  const allFeedbacks = feedbackRequests.filter(req => req.status === 'approved').map(req => ({
    id: req.id,
    from: req.from,
    to: req.to,
    date: req.date,
    type: req.type,
    message: req.message
  }));
  // Filter feedbacks for the current user (employee view)
  const feedbacks = isManager ? allFeedbacks : allFeedbacks.filter(feedback => feedback.to === userName);
  const [oneOnOnes, setOneOnOnes] = useState<OneOnOne[]>([{
    id: 1,
    with: 'Jean Martin (Superviseur)',
    date: '2023-12-15',
    status: 'upcoming',
    agenda: [{
      id: 1,
      text: 'Revue des objectifs Q4',
      duration: 20,
      owner: 'Jean Martin',
      status: 'pending'
    }, {
      id: 2,
      text: 'Planification des projets 2024',
      duration: 25,
      owner: 'Marie Dupont',
      status: 'pending'
    }, {
      id: 3,
      text: 'Formation en gestion de chantier',
      duration: 15,
      owner: 'Jean Martin',
      status: 'pending'
    }],
    anonymousComments: [{
      id: 1,
      text: "J'aimerais discuter des opportunités de développement de carrière",
      timestamp: '2023-12-10 14:30',
      isRead: false
    }, {
      id: 2,
      text: 'Besoin de clarifier les attentes sur le nouveau projet',
      timestamp: '2023-12-11 09:15',
      isRead: true
    }]
  }, {
    id: 2,
    with: 'Jean Martin (Superviseur)',
    date: '2023-11-15',
    status: 'completed',
    summary: 'Discussion sur les objectifs Q4, identification des défis sur le projet commercial, actions pour améliorer la coordination avec les sous-traitants.',
    anonymousComments: []
  }]);
  const developmentPlan = {
    skills: [{
      id: 1,
      name: 'Gestion de chantier',
      target: 'Niveau avancé',
      status: 'in-progress'
    }, {
      id: 2,
      name: 'Sécurité et conformité',
      target: 'Certification SST',
      status: 'in-progress'
    }],
    actions: [{
      id: 1,
      title: 'Formation en gestion de projet de construction',
      dueDate: '2024-01-31',
      status: 'not-started'
    }, {
      id: 2,
      title: 'Certification SST (Santé et Sécurité au Travail)',
      dueDate: '2024-02-28',
      status: 'in-progress'
    }, {
      id: 3,
      title: 'Mentorat avec contremaître senior',
      dueDate: 'En cours',
      status: 'in-progress'
    }]
  };
  // Manager-specific data
  const teamObjectives = [{
    id: 1,
    employee: 'Marie Dupont',
    title: 'Réduire les incidents de sécurité sur les chantiers',
    progress: 85,
    status: 'on-track'
  }, {
    id: 2,
    employee: 'Thomas Petit',
    title: 'Améliorer la qualité des finitions',
    progress: 65,
    status: 'at-risk'
  }, {
    id: 3,
    employee: 'Sophie Bernard',
    title: 'Optimiser la gestion des matériaux et inventaire',
    progress: 90,
    status: 'on-track'
  }];
  const teamFeedbacks = allFeedbacks.filter(f => f.from === 'Vous');
  const [teamOneOnOnes, setTeamOneOnOnes] = useState<OneOnOne[]>([{
    id: 1,
    with: 'Marie Dupont',
    date: '2023-12-15',
    status: 'upcoming',
    agenda: [{
      id: 1,
      text: 'Revue objectifs Q4',
      duration: 15,
      owner: 'Manager',
      status: 'pending'
    }, {
      id: 2,
      text: 'Projets 2024',
      duration: 20,
      owner: 'Marie Dupont',
      status: 'pending'
    }, {
      id: 3,
      text: 'Formation gestion de chantier',
      duration: 10,
      owner: 'Manager',
      status: 'pending'
    }],
    anonymousComments: [{
      id: 1,
      text: "J'aimerais discuter des opportunités de développement de carrière",
      timestamp: '2023-12-10 14:30',
      isRead: false
    }, {
      id: 2,
      text: 'Besoin de clarifier les attentes sur le nouveau projet',
      timestamp: '2023-12-11 09:15',
      isRead: false
    }]
  }, {
    id: 2,
    with: 'Thomas Petit',
    date: '2023-12-16',
    status: 'upcoming',
    agenda: [{
      id: 1,
      text: 'Performance récente',
      duration: 15,
      owner: 'Manager',
      status: 'pending'
    }, {
      id: 2,
      text: 'Défis techniques',
      duration: 20,
      owner: 'Thomas Petit',
      status: 'pending'
    }, {
      id: 3,
      text: 'Formation qualité',
      duration: 10,
      owner: 'Manager',
      status: 'pending'
    }],
    anonymousComments: []
  }]);
  const teamDevelopment = [{
    id: 1,
    employee: 'Marie Dupont',
    skills: ['Gestion de chantier', 'Sécurité SST'],
    actions: 2,
    progress: 75
  }, {
    id: 2,
    employee: 'Thomas Petit',
    skills: ['Contrôle qualité', 'Lecture de plans'],
    actions: 3,
    progress: 60
  }, {
    id: 3,
    employee: 'Sophie Bernard',
    skills: ['Gestion inventaire', 'Coordination sous-traitants'],
    actions: 2,
    progress: 80
  }];
  const teamMembers = ['Thomas Petit', 'Sophie Bernard', 'Luc Tremblay', 'Jean Martin'];
  const handleMarkAsRead = (feedbackId: number) => {
    setReadFeedbacks([...readFeedbacks, feedbackId]);
    showToast('Feedback marqué comme lu', 'success');
  };
  const handleUpdatePDI = () => {
    if (isEditingPDI) {
      showToast('Plan de développement mis à jour avec succès', 'success');
    }
    setIsEditingPDI(!isEditingPDI);
  };
  // Feedback functions
  const handleOpenSendFeedbackModal = () => {
    setFeedbackRecipient('');
    setFeedbackType('positive');
    setFeedbackMessage('');
    setIsSendFeedbackModalOpen(true);
  };
  const handleSendFeedback = () => {
    if (!feedbackRecipient || !feedbackMessage.trim()) {
      showToast('Veuillez remplir tous les champs', 'error');
      return;
    }
    const newFeedback: FeedbackRequest = {
      id: Date.now(),
      from: userName,
      to: feedbackRecipient,
      date: new Date().toISOString().split('T')[0],
      type: feedbackType,
      message: feedbackMessage.trim(),
      status: 'pending'
    };
    setFeedbackRequests([...feedbackRequests, newFeedback]);
    setIsSendFeedbackModalOpen(false);
    showToast('Feedback envoyé au gestionnaire pour validation', 'success');
  };
  const handleOpenReviewFeedbackModal = (feedback: FeedbackRequest) => {
    setReviewingFeedback(feedback);
    setManagerComment('');
    setIsReviewFeedbackModalOpen(true);
  };
  const handleApproveFeedback = () => {
    if (!reviewingFeedback) return;
    setFeedbackRequests(feedbackRequests.map(f => f.id === reviewingFeedback.id ? {
      ...f,
      status: 'approved',
      managerComment
    } : f));
    setIsReviewFeedbackModalOpen(false);
    setReviewingFeedback(null);
    setManagerComment('');
    showToast('Feedback approuvé et envoyé', 'success');
  };
  const handleRejectFeedback = () => {
    if (!reviewingFeedback) return;
    setFeedbackRequests(feedbackRequests.map(f => f.id === reviewingFeedback.id ? {
      ...f,
      status: 'rejected',
      managerComment
    } : f));
    setIsReviewFeedbackModalOpen(false);
    setReviewingFeedback(null);
    setManagerComment('');
    showToast('Feedback rejeté', 'success');
  };
  const getPendingFeedbackCount = () => {
    return feedbackRequests.filter(f => f.status === 'pending').length;
  };
  // Agenda editing functions
  const handleOpenAgendaModal = (oneOnOneId: number) => {
    const oneOnOne = isManager ? teamOneOnOnes.find(o => o.id === oneOnOneId) : oneOnOnes.find(o => o.id === oneOnOneId);
    if (oneOnOne && oneOnOne.agenda) {
      setEditingOneOnOneId(oneOnOneId);
      setTempAgenda([...oneOnOne.agenda]);
      setIsAgendaModalOpen(true);
    }
  };
  const handleAddAgendaItem = () => {
    if (newAgendaItem.trim()) {
      const newItem: AgendaItem = {
        id: Date.now(),
        text: newAgendaItem.trim(),
        duration: newAgendaDuration,
        owner: newAgendaOwner || (isManager ? 'Manager' : userName),
        status: 'pending'
      };
      setTempAgenda([...tempAgenda, newItem]);
      setNewAgendaItem('');
      setNewAgendaDuration(15);
      setNewAgendaOwner('');
      showToast('Élément ajouté', 'success');
    }
  };
  const handleRemoveAgendaItem = (itemId: number) => {
    setTempAgenda(tempAgenda.filter(item => item.id !== itemId));
    showToast('Élément supprimé', 'success');
  };
  const handleStartEditItem = (item: AgendaItem) => {
    setEditingItemId(item.id);
    setEditingItemText(item.text);
  };
  const handleSaveEditItem = (itemId: number) => {
    setTempAgenda(tempAgenda.map(item => item.id === itemId ? {
      ...item,
      text: editingItemText
    } : item));
    setEditingItemId(null);
    setEditingItemText('');
    showToast('Élément modifié', 'success');
  };
  const handleCancelEditItem = () => {
    setEditingItemId(null);
    setEditingItemText('');
  };
  const handleToggleStatus = (itemId: number) => {
    setTempAgenda(tempAgenda.map(item => {
      if (item.id === itemId) {
        const newStatus = item.status === 'pending' ? 'completed' : 'pending';
        return {
          ...item,
          status: newStatus
        };
      }
      return item;
    }));
  };
  const handleUpdateDuration = (itemId: number, duration: number) => {
    setTempAgenda(tempAgenda.map(item => item.id === itemId ? {
      ...item,
      duration
    } : item));
  };
  const handleDragStart = (itemId: number) => {
    setDraggedItem(itemId);
  };
  const handleDragOver = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === targetId) return;
    const draggedIndex = tempAgenda.findIndex(item => item.id === draggedItem);
    const targetIndex = tempAgenda.findIndex(item => item.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const newAgenda = [...tempAgenda];
    const [removed] = newAgenda.splice(draggedIndex, 1);
    newAgenda.splice(targetIndex, 0, removed);
    setTempAgenda(newAgenda);
  };
  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  const handleSaveAgenda = () => {
    if (editingOneOnOneId === null) return;
    if (isManager) {
      setTeamOneOnOnes(teamOneOnOnes.map(o => o.id === editingOneOnOneId ? {
        ...o,
        agenda: tempAgenda
      } : o));
    } else {
      setOneOnOnes(oneOnOnes.map(o => o.id === editingOneOnOneId ? {
        ...o,
        agenda: tempAgenda
      } : o));
    }
    setIsAgendaModalOpen(false);
    setEditingOneOnOneId(null);
    setTempAgenda([]);
    showToast('Ordre du jour mis à jour', 'success');
  };
  const getTotalDuration = () => {
    return tempAgenda.reduce((total, item) => total + (item.duration || 0), 0);
  };
  // Anonymous comments functions
  const handleOpenCommentsModal = (oneOnOneId: number) => {
    setViewingCommentsOneOnOneId(oneOnOneId);
    setIsCommentsModalOpen(true);
  };
  const handleAddAnonymousComment = () => {
    if (!newAnonymousComment.trim() || viewingCommentsOneOnOneId === null) return;
    const newComment: AnonymousComment = {
      id: Date.now(),
      text: newAnonymousComment.trim(),
      timestamp: new Date().toLocaleString('fr-CA'),
      isRead: false
    };
    if (isManager) {
      setTeamOneOnOnes(teamOneOnOnes.map(o => o.id === viewingCommentsOneOnOneId ? {
        ...o,
        anonymousComments: [...(o.anonymousComments || []), newComment]
      } : o));
    } else {
      setOneOnOnes(oneOnOnes.map(o => o.id === viewingCommentsOneOnOneId ? {
        ...o,
        anonymousComments: [...(o.anonymousComments || []), newComment]
      } : o));
    }
    setNewAnonymousComment('');
    showToast('Commentaire anonyme ajouté', 'success');
  };
  const handleMarkCommentAsRead = (commentId: number) => {
    if (viewingCommentsOneOnOneId === null) return;
    if (isManager) {
      setTeamOneOnOnes(teamOneOnOnes.map(o => o.id === viewingCommentsOneOnOneId ? {
        ...o,
        anonymousComments: o.anonymousComments?.map(c => c.id === commentId ? {
          ...c,
          isRead: true
        } : c)
      } : o));
    } else {
      setOneOnOnes(oneOnOnes.map(o => o.id === viewingCommentsOneOnOneId ? {
        ...o,
        anonymousComments: o.anonymousComments?.map(c => c.id === commentId ? {
          ...c,
          isRead: true
        } : c)
      } : o));
    }
    showToast('Commentaire marqué comme lu', 'success');
  };
  const getUnreadCommentsCount = (oneOnOne: OneOnOne) => {
    return oneOnOne.anonymousComments?.filter(c => !c.isRead).length || 0;
  };
  const getCurrentOneOnOneComments = () => {
    if (viewingCommentsOneOnOneId === null) return [];
    const oneOnOne = isManager ? teamOneOnOnes.find(o => o.id === viewingCommentsOneOnOneId) : oneOnOnes.find(o => o.id === viewingCommentsOneOnOneId);
    return oneOnOne?.anonymousComments || [];
  };
  return <div className="pb-20 md:pb-0" data-id="element-985">
      <div className="mb-8" data-id="element-986">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-987">
          {isManager ? 'Performance & Développement - Équipe' : 'Performance & Développement'}
        </h1>
        <p className="text-gray-600" data-id="element-988">
          {isManager ? 'Suivez la performance et le développement de votre équipe' : 'Suivez vos objectifs, recevez du feedback et développez vos compétences'}
        </p>
      </div>

      {/* Alerte pour feedbacks en attente (gestionnaire) */}
      {isManager && getPendingFeedbackCount() > 0 && <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg" data-id="element-989">
          <div className="flex items-start" data-id="element-990">
            <MessageSquare className="h-5 w-5 text-blue-600 mt-0.5 mr-3" data-id="element-991" />
            <div data-id="element-992">
              <h3 className="text-sm font-medium text-blue-800" data-id="element-993">
                {getPendingFeedbackCount()} feedback
                {getPendingFeedbackCount() > 1 ? 's' : ''} en attente de
                validation
              </h3>
              <p className="text-sm text-blue-700 mt-1" data-id="element-994">
                Des employés ont envoyé du feedback qui nécessite votre
                approbation.
              </p>
            </div>
          </div>
        </div>}

      <div className="bg-white rounded-lg shadow-sm" data-id="element-995">
        {/* Mobile: Dropdown for tabs */}
        <div className="md:hidden p-4 border-b border-gray-200" data-id="element-996">
          <select value={activeTab} onChange={e => setActiveTab(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-997">
            <option value="objectives" data-id="element-998">
              {isManager ? 'Objectifs équipe' : 'Objectifs'}
            </option>
            <option value="feedback" data-id="element-999">Feedback</option>
            <option value="oneOnOne" data-id="element-1000">1:1</option>
            <option value="development" data-id="element-1001">
              {isManager ? 'Développement équipe' : 'Plan de développement'}
            </option>
          </select>
        </div>

        {/* Desktop: Tab navigation */}
        <nav className="hidden md:flex border-b border-gray-200" data-id="element-1002">
          <button onClick={() => setActiveTab('objectives')} className={`px-6 py-4 font-medium text-sm flex items-center transition-colors ${activeTab === 'objectives' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1003">
            <Target className="h-4 w-4 mr-2" data-id="element-1004" />
            {isManager ? 'Objectifs équipe' : 'Objectifs'}
          </button>
          <button onClick={() => setActiveTab('feedback')} className={`px-6 py-4 font-medium text-sm flex items-center transition-colors relative ${activeTab === 'feedback' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1005">
            <MessageSquare className="h-4 w-4 mr-2" data-id="element-1006" />
            Feedback
            {isManager && getPendingFeedbackCount() > 0 && <span className="ml-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-id="element-1007">
                {getPendingFeedbackCount()}
              </span>}
          </button>
          <button onClick={() => setActiveTab('oneOnOne')} className={`px-6 py-4 font-medium text-sm flex items-center transition-colors ${activeTab === 'oneOnOne' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1008">
            <Calendar className="h-4 w-4 mr-2" data-id="element-1009" />
            1:1
          </button>
          <button onClick={() => setActiveTab('development')} className={`px-6 py-4 font-medium text-sm flex items-center transition-colors ${activeTab === 'development' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1010">
            <BookOpen className="h-4 w-4 mr-2" data-id="element-1011" />
            {isManager ? 'Développement équipe' : 'Plan de développement'}
          </button>
        </nav>

        <div className="p-6" data-id="element-1012">
          {activeTab === 'objectives' && <div data-id="element-1013">
              {isManager ? <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" data-id="element-1014">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-1015">
                      Objectifs de l'équipe
                    </h2>
                    <button className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm hover:shadow transition-all" data-id="element-1016">
                      + Créer un objectif équipe
                    </button>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1017">
                    {teamObjectives.map(objective => <div key={objective.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1018">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1019">
                          <div data-id="element-1020">
                            <h3 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1021">
                              {objective.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1" data-id="element-1022">
                              <Users className="h-4 w-4 inline mr-1" data-id="element-1023" />
                              {objective.employee}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap self-start ${objective.status === 'on-track' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1024">
                            {objective.status === 'on-track' ? 'Sur la bonne voie' : 'À risque'}
                          </span>
                        </div>
                        <div className="mb-3" data-id="element-1025">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1026">
                            <span className="text-gray-600" data-id="element-1027">Progression</span>
                            <span className="font-medium" data-id="element-1028">
                              {objective.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1029">
                            <div className={`h-2.5 rounded-full ${objective.status === 'on-track' ? 'bg-green-600' : 'bg-amber-600'}`} style={{
                      width: `${objective.progress}%`
                    }} data-id="element-1030"></div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2" data-id="element-1031">
                          <button className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1032">
                            Voir détails
                          </button>
                          <button className="w-full sm:w-auto px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200" data-id="element-1033">
                            Donner feedback
                          </button>
                        </div>
                      </div>)}
                  </div>
                </> : <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" data-id="element-1034">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-1035">
                      Mes objectifs
                    </h2>
                    <button className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm hover:shadow transition-all" data-id="element-1036">
                      + Proposer un objectif
                    </button>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1037">
                    {objectives.map(objective => <div key={objective.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1038">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1039">
                          <h3 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1040">
                            {objective.title}
                          </h3>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs whitespace-nowrap self-start" data-id="element-1041">
                            Q4 2023
                          </span>
                        </div>
                        <div className="mb-3" data-id="element-1042">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1043">
                            <span className="text-gray-600" data-id="element-1044">Progression</span>
                            <span className="font-medium" data-id="element-1045">
                              {objective.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1046">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{
                      width: `${objective.progress}%`
                    }} data-id="element-1047"></div>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 text-sm mb-4" data-id="element-1048">
                          <div className="flex flex-col sm:flex-row sm:items-start" data-id="element-1049">
                            <span className="text-gray-500 font-medium sm:min-w-24" data-id="element-1050">
                              Aligné à:
                            </span>
                            <span className="text-gray-900" data-id="element-1051">
                              {objective.alignedTo}
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-start" data-id="element-1052">
                            <span className="text-gray-500 font-medium sm:min-w-24" data-id="element-1053">
                              Échéance:
                            </span>
                            <span className="text-gray-900" data-id="element-1054">
                              {objective.dueDate}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2" data-id="element-1055">
                          <button className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1056">
                            Mettre à jour
                          </button>
                          <button className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1057">
                            Check-in
                          </button>
                        </div>
                      </div>)}
                  </div>
                </>}
            </div>}

          {activeTab === 'feedback' && <div data-id="element-1058">
              {isManager ? <>
                  {/* Section de validation des feedbacks */}
                  {getPendingFeedbackCount() > 0 && <div className="mb-8" data-id="element-1059">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1060">
                        Feedbacks en attente de validation (
                        {getPendingFeedbackCount()})
                      </h2>
                      <div className="space-y-4" data-id="element-1061">
                        {feedbackRequests.filter(f => f.status === 'pending').map(feedback => <div key={feedback.id} className="border-2 border-blue-200 bg-blue-50 p-4 rounded-lg" data-id="element-1062">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1063">
                                <div data-id="element-1064">
                                  <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1065">
                                    De {feedback.from} → À {feedback.to}
                                  </span>
                                  <span className="text-xs md:text-sm text-gray-500 block sm:inline sm:ml-2 mt-1 sm:mt-0" data-id="element-1066">
                                    {feedback.date}
                                  </span>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs self-start ${feedback.type === 'positive' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1067">
                                  {feedback.type === 'positive' ? 'Positif' : 'Constructif'}
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm md:text-base mb-4" data-id="element-1068">
                                {feedback.message}
                              </p>
                              <div className="flex flex-col sm:flex-row gap-2" data-id="element-1069">
                                <button onClick={() => handleOpenReviewFeedbackModal(feedback)} className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-2" data-id="element-1070">
                                  <Eye className="h-4 w-4" data-id="element-1071" />
                                  Examiner
                                </button>
                              </div>
                            </div>)}
                      </div>
                    </div>}

                  {/* Section des feedbacks approuvés */}
                  <div className="flex flex-col gap-3 mb-4 md:mb-6" data-id="element-1072">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1073">
                      Feedback équipe
                    </h2>
                    <button className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-1074">
                      + Donner du feedback
                    </button>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1075">
                    {teamFeedbacks.map(feedback => <div key={feedback.id} className={`border-l-4 ${feedback.type === 'positive' ? 'border-green-500' : 'border-amber-500'} bg-gray-50 p-4 rounded-r-lg`} data-id="element-1076">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1077">
                          <div data-id="element-1078">
                            <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1079">
                              À {feedback.to}
                            </span>
                            <span className="text-xs md:text-sm text-gray-500 block sm:inline sm:ml-2 mt-1 sm:mt-0" data-id="element-1080">
                              {feedback.date}
                            </span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs self-start ${feedback.type === 'positive' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1081">
                            {feedback.type === 'positive' ? 'Positif' : 'Constructif'}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm md:text-base mb-3" data-id="element-1082">
                          {feedback.message}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2" data-id="element-1083">
                          <button className="w-full sm:w-auto text-sm text-blue-600 hover:text-blue-800 font-medium py-2 sm:py-0" data-id="element-1084">
                            Voir conversation
                          </button>
                          <button className="w-full sm:w-auto text-sm text-blue-600 hover:text-blue-800 font-medium py-2 sm:py-0" data-id="element-1085">
                            Ajouter un suivi
                          </button>
                        </div>
                      </div>)}
                  </div>
                </> : <>
                  <div className="flex flex-col gap-3 mb-4 md:mb-6" data-id="element-1086">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1087">
                      Feedback
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2" data-id="element-1088">
                      <button className="w-full sm:w-auto px-4 py-2.5 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200" data-id="element-1089">
                        Demander du feedback
                      </button>
                      <button onClick={handleOpenSendFeedbackModal} className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-1090">
                        Donner du feedback
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1091">
                    {feedbacks.map(feedback => {
                const isRead = readFeedbacks.includes(feedback.id);
                return <div key={feedback.id} className={`border-l-4 ${feedback.type === 'positive' ? 'border-green-500' : 'border-amber-500'} ${isRead ? 'bg-gray-100 opacity-75' : 'bg-gray-50'} p-4 rounded-r-lg relative`} data-id="element-1092">
                          {isRead && <div className="absolute top-2 right-2" data-id="element-1093">
                              <span className="flex items-center text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full" data-id="element-1094">
                                <Check className="h-3 w-3 mr-1" data-id="element-1095" />
                                Lu
                              </span>
                            </div>}
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1096">
                            <div data-id="element-1097">
                              <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1098">
                                De {feedback.from}
                              </span>
                              <span className="text-xs md:text-sm text-gray-500 block sm:inline sm:ml-2 mt-1 sm:mt-0" data-id="element-1099">
                                {feedback.date}
                              </span>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs self-start ${feedback.type === 'positive' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1100">
                              {feedback.type === 'positive' ? 'Positif' : 'Constructif'}
                            </span>
                          </div>
                          <p className="text-gray-700 text-sm md:text-base mb-3" data-id="element-1101">
                            {feedback.message}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2" data-id="element-1102">
                            {!isRead && <button onClick={() => handleMarkAsRead(feedback.id)} className="w-full sm:w-auto text-sm text-green-600 hover:text-green-800 font-medium py-2 sm:py-0 flex items-center justify-center sm:justify-start" data-id="element-1103">
                                <Check className="h-4 w-4 mr-1" data-id="element-1104" />
                                Marquer comme lu
                              </button>}
                            <button className="w-full sm:w-auto text-sm text-blue-600 hover:text-blue-800 font-medium py-2 sm:py-0" data-id="element-1105">
                              Remercier
                            </button>
                            <button className="w-full sm:w-auto text-sm text-blue-600 hover:text-blue-800 font-medium py-2 sm:py-0" data-id="element-1106">
                              Lier à un objectif
                            </button>
                          </div>
                        </div>;
              })}
                  </div>
                </>}
            </div>}

          {activeTab === 'oneOnOne' && <div data-id="element-1107">
              {isManager ? <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6" data-id="element-1108">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1109">
                      1:1 avec l'équipe
                    </h2>
                    <button className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-1110">
                      Planifier un 1:1
                    </button>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1111">
                    {teamOneOnOnes.map(oneOnOne => {
                const unreadCount = getUnreadCommentsCount(oneOnOne);
                return <div key={oneOnOne.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1112">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1113">
                            <div data-id="element-1114">
                              <h3 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1115">
                                {oneOnOne.with}
                              </h3>
                              <div className="flex items-center text-xs md:text-sm text-gray-600 mt-1" data-id="element-1116">
                                <Calendar className="h-4 w-4 mr-1" data-id="element-1117" />
                                <span data-id="element-1118">{oneOnOne.date}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1119">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs self-start" data-id="element-1120">
                                À venir
                              </span>
                              {unreadCount > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium" data-id="element-1121">
                                  {unreadCount} nouveau
                                  {unreadCount > 1 ? 'x' : ''}
                                </span>}
                            </div>
                          </div>
                          <div data-id="element-1122">
                            <div className="flex items-center justify-between mb-2" data-id="element-1123">
                              <h4 className="text-sm font-medium text-gray-700" data-id="element-1124">
                                Ordre du jour:
                              </h4>
                              <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1125">
                                <Clock className="h-3 w-3" data-id="element-1126" />
                                {oneOnOne.agenda?.reduce((total, item) => total + (item.duration || 0), 0)}{' '}
                                min
                              </span>
                            </div>
                            <ul className="space-y-2 mb-4" data-id="element-1127">
                              {oneOnOne.agenda?.map(item => <li key={item.id} className="flex items-start gap-2 text-sm" data-id="element-1128">
                                  <span className="text-gray-400 mt-0.5" data-id="element-1129">
                                    •
                                  </span>
                                  <div className="flex-1" data-id="element-1130">
                                    <span className="text-gray-900" data-id="element-1131">
                                      {item.text}
                                    </span>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500" data-id="element-1132">
                                      <span className="flex items-center gap-1" data-id="element-1133">
                                        <Clock className="h-3 w-3" data-id="element-1134" />
                                        {item.duration} min
                                      </span>
                                      <span data-id="element-1135">•</span>
                                      <span data-id="element-1136">{item.owner}</span>
                                    </div>
                                  </div>
                                </li>)}
                            </ul>
                            <div className="flex flex-col sm:flex-row gap-2" data-id="element-1137">
                              <button className="w-full sm:w-auto px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200" data-id="element-1138">
                                Préparer la réunion
                              </button>
                              <button onClick={() => handleOpenAgendaModal(oneOnOne.id)} className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1139">
                                Modifier l'agenda
                              </button>
                              <button onClick={() => handleOpenCommentsModal(oneOnOne.id)} className="w-full sm:w-auto px-4 py-2 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 flex items-center justify-center gap-2" data-id="element-1140">
                                <Lock className="h-4 w-4" data-id="element-1141" />
                                Commentaires anonymes
                                {unreadCount > 0 && <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-id="element-1142">
                                    {unreadCount}
                                  </span>}
                              </button>
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </> : <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6" data-id="element-1143">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1144">
                      Mes 1:1
                    </h2>
                    <button className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-1145">
                      Planifier un 1:1
                    </button>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1146">
                    {oneOnOnes.map(oneOnOne => {
                const unreadCount = getUnreadCommentsCount(oneOnOne);
                return <div key={oneOnOne.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1147">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1148">
                            <div data-id="element-1149">
                              <h3 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1150">
                                {oneOnOne.with}
                              </h3>
                              <div className="flex items-center text-xs md:text-sm text-gray-600 mt-1" data-id="element-1151">
                                <Calendar className="h-4 w-4 mr-1" data-id="element-1152" />
                                <span data-id="element-1153">{oneOnOne.date}</span>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs self-start ${oneOnOne.status === 'upcoming' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-1154">
                              {oneOnOne.status === 'upcoming' ? 'À venir' : 'Complété'}
                            </span>
                          </div>
                          {oneOnOne.status === 'upcoming' ? <div data-id="element-1155">
                              <div className="flex items-center justify-between mb-2" data-id="element-1156">
                                <h4 className="text-sm font-medium text-gray-700" data-id="element-1157">
                                  Ordre du jour:
                                </h4>
                                <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1158">
                                  <Clock className="h-3 w-3" data-id="element-1159" />
                                  {oneOnOne.agenda?.reduce((total, item) => total + (item.duration || 0), 0)}{' '}
                                  min
                                </span>
                              </div>
                              <ul className="space-y-2 mb-4" data-id="element-1160">
                                {oneOnOne.agenda?.map(item => <li key={item.id} className="flex items-start gap-2 text-sm" data-id="element-1161">
                                    <span className="text-gray-400 mt-0.5" data-id="element-1162">
                                      •
                                    </span>
                                    <div className="flex-1" data-id="element-1163">
                                      <span className="text-gray-900" data-id="element-1164">
                                        {item.text}
                                      </span>
                                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500" data-id="element-1165">
                                        <span className="flex items-center gap-1" data-id="element-1166">
                                          <Clock className="h-3 w-3" data-id="element-1167" />
                                          {item.duration} min
                                        </span>
                                        <span data-id="element-1168">•</span>
                                        <span data-id="element-1169">{item.owner}</span>
                                      </div>
                                    </div>
                                  </li>)}
                              </ul>
                              <div className="flex flex-col sm:flex-row gap-2" data-id="element-1170">
                                <button onClick={() => handleOpenAgendaModal(oneOnOne.id)} className="w-full sm:w-auto px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200" data-id="element-1171">
                                  Modifier l'ordre du jour
                                </button>
                                <button onClick={() => handleOpenCommentsModal(oneOnOne.id)} className="w-full sm:w-auto px-4 py-2 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 flex items-center justify-center gap-2" data-id="element-1172">
                                  <Lock className="h-4 w-4" data-id="element-1173" />
                                  Commentaires anonymes
                                </button>
                              </div>
                            </div> : <div data-id="element-1174">
                              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1175">
                                Résumé:
                              </h4>
                              <p className="text-sm text-gray-600 mb-4" data-id="element-1176">
                                {oneOnOne.summary}
                              </p>
                              <button className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1177">
                                Voir les détails
                              </button>
                            </div>}
                        </div>;
              })}
                  </div>
                </>}
            </div>}

          {activeTab === 'development' && <div data-id="element-1178">
              {isManager ? <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6" data-id="element-1179">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1180">
                      Développement de l'équipe
                    </h2>
                  </div>
                  <div className="space-y-4 md:space-y-6" data-id="element-1181">
                    {teamDevelopment.map(member => <div key={member.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1182">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3" data-id="element-1183">
                          <div data-id="element-1184">
                            <h3 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1185">
                              {member.employee}
                            </h3>
                            <div className="flex flex-wrap gap-1 mt-2" data-id="element-1186">
                              {member.skills.map((skill, index) => <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs" data-id="element-1187">
                                  {skill}
                                </span>)}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs whitespace-nowrap self-start" data-id="element-1188">
                            {member.actions} actions actives
                          </span>
                        </div>
                        <div className="mb-3" data-id="element-1189">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1190">
                            <span className="text-gray-600" data-id="element-1191">
                              Progression globale
                            </span>
                            <span className="font-medium" data-id="element-1192">
                              {member.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1193">
                            <div className="bg-blue-600 h-2.5 rounded-full" style={{
                      width: `${member.progress}%`
                    }} data-id="element-1194"></div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2" data-id="element-1195">
                          <button className="w-full sm:w-auto px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200" data-id="element-1196">
                            Voir le PDI
                          </button>
                          <button className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200" data-id="element-1197">
                            Suggérer des actions
                          </button>
                        </div>
                      </div>)}
                  </div>
                </> : <>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6" data-id="element-1198">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900" data-id="element-1199">
                      Plan de développement individuel
                    </h2>
                    <button onClick={handleUpdatePDI} className={`w-full sm:w-auto px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${isEditingPDI ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-1200">
                      {isEditingPDI ? 'Enregistrer' : 'Mettre à jour mon PDI'}
                    </button>
                  </div>
                  {isEditingPDI && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-1201">
                      <p className="text-xs md:text-sm text-blue-800" data-id="element-1202">
                        Mode édition activé. Modifiez vos compétences cibles et
                        actions de développement, puis cliquez sur
                        "Enregistrer".
                      </p>
                    </div>}
                  <div className="space-y-6" data-id="element-1203">
                    <div data-id="element-1204">
                      <h3 className="text-sm md:text-base font-medium text-gray-900 mb-3" data-id="element-1205">
                        Compétences cibles
                      </h3>
                      <div className="space-y-3 md:space-y-4" data-id="element-1206">
                        {developmentPlan.skills.map(skill => <div key={skill.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1207">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2" data-id="element-1208">
                              <h4 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1209">
                                {skill.name}
                              </h4>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs self-start" data-id="element-1210">
                                En cours
                              </span>
                            </div>
                            <p className="text-xs md:text-sm text-gray-600" data-id="element-1211">
                              Objectif: {skill.target}
                            </p>
                          </div>)}
                      </div>
                    </div>
                    <div data-id="element-1212">
                      <h3 className="text-sm md:text-base font-medium text-gray-900 mb-3" data-id="element-1213">
                        Actions de développement
                      </h3>
                      <div className="space-y-3" data-id="element-1214">
                        {developmentPlan.actions.map(action => <div key={action.id} className="border border-gray-200 rounded-lg p-3 md:p-4" data-id="element-1215">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2" data-id="element-1216">
                              <h4 className="font-medium text-gray-900 text-sm md:text-base" data-id="element-1217">
                                {action.title}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs self-start ${action.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : action.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1218">
                                {action.status === 'in-progress' ? 'En cours' : action.status === 'completed' ? 'Terminé' : 'À démarrer'}
                              </span>
                            </div>
                            <div className="flex items-center text-xs md:text-sm text-gray-600 mt-2" data-id="element-1219">
                              <Calendar className="h-4 w-4 mr-1" data-id="element-1220" />
                              <span data-id="element-1221">Échéance: {action.dueDate}</span>
                            </div>
                          </div>)}
                        <button className="w-full py-3 mt-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-500" data-id="element-1222">
                          + Ajouter une action
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg" data-id="element-1223">
                    <h3 className="text-sm md:text-base font-medium text-blue-800 mb-3 flex items-center" data-id="element-1224">
                      <Award className="h-5 w-5 mr-2" data-id="element-1225" />
                      Opportunités recommandées
                    </h3>
                    <ul className="space-y-3" data-id="element-1226">
                      <li className="flex items-start" data-id="element-1227">
                        <span className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 text-xs mr-2 mt-0.5 flex-shrink-0" data-id="element-1228">
                          1
                        </span>
                        <div data-id="element-1229">
                          <p className="text-xs md:text-sm font-medium text-gray-900" data-id="element-1230">
                            Formation "Communication efficace en équipe"
                          </p>
                          <p className="text-xs text-gray-600 mt-1" data-id="element-1231">
                            Recommandé pour développer votre compétence en
                            Communication
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start" data-id="element-1232">
                        <span className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 text-xs mr-2 mt-0.5 flex-shrink-0" data-id="element-1233">
                          2
                        </span>
                        <div data-id="element-1234">
                          <p className="text-xs md:text-sm font-medium text-gray-900" data-id="element-1235">
                            Mission interne: Animation d'ateliers d'innovation
                          </p>
                          <p className="text-xs text-gray-600 mt-1" data-id="element-1236">
                            Recommandé pour développer votre compétence en
                            Leadership
                          </p>
                        </div>
                      </li>
                    </ul>
                    <button className="text-xs md:text-sm text-blue-700 mt-3 hover:text-blue-900 font-medium" data-id="element-1237">
                      Voir toutes les opportunités
                    </button>
                  </div>
                </>}
            </div>}
        </div>
      </div>

      {/* Modal pour envoyer du feedback (employé) */}
      <Modal isOpen={isSendFeedbackModalOpen} onClose={() => setIsSendFeedbackModalOpen(false)} title="Envoyer du feedback à un collègue" size="lg" data-id="element-1238">
        <div className="space-y-6" data-id="element-1239">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-1240">
            <div className="flex items-start gap-3" data-id="element-1241">
              <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1242" />
              <div data-id="element-1243">
                <h3 className="font-semibold text-blue-900 mb-1" data-id="element-1244">
                  Feedback entre collègues
                </h3>
                <p className="text-sm text-blue-700 leading-relaxed" data-id="element-1245">
                  Votre feedback sera envoyé à votre gestionnaire pour
                  validation avant d'être transmis à votre collègue. Cela
                  garantit un environnement de travail respectueux et
                  constructif.
                </p>
              </div>
            </div>
          </div>

          <div data-id="element-1246">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1247">
              Destinataire
            </label>
            <select value={feedbackRecipient} onChange={e => setFeedbackRecipient(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1248">
              <option value="" data-id="element-1249">Sélectionner un collègue...</option>
              {teamMembers.map(member => <option key={member} value={member} data-id="element-1250">
                  {member}
                </option>)}
            </select>
          </div>

          <div data-id="element-1251">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1252">
              Type de feedback
            </label>
            <div className="flex gap-3" data-id="element-1253">
              <button onClick={() => setFeedbackType('positive')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feedbackType === 'positive' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`} data-id="element-1254">
                <ThumbsUp className={`h-5 w-5 mx-auto mb-1 ${feedbackType === 'positive' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-1255" />
                <span className={`text-sm font-medium ${feedbackType === 'positive' ? 'text-green-900' : 'text-gray-600'}`} data-id="element-1256">
                  Positif
                </span>
              </button>
              <button onClick={() => setFeedbackType('constructive')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feedbackType === 'constructive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`} data-id="element-1257">
                <ThumbsDown className={`h-5 w-5 mx-auto mb-1 ${feedbackType === 'constructive' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-1258" />
                <span className={`text-sm font-medium ${feedbackType === 'constructive' ? 'text-amber-900' : 'text-gray-600'}`} data-id="element-1259">
                  Constructif
                </span>
              </button>
            </div>
          </div>

          <div data-id="element-1260">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1261">
              Votre message
            </label>
            <textarea value={feedbackMessage} onChange={e => setFeedbackMessage(e.target.value)} placeholder="Décrivez votre feedback de manière claire et respectueuse..." rows={5} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-1262" />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-1263">
            <button onClick={() => setIsSendFeedbackModalOpen(false)} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1264">
              Annuler
            </button>
            <button onClick={handleSendFeedback} className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-1265">
              <Send className="h-4 w-4" data-id="element-1266" />
              Envoyer pour validation
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal pour examiner/valider du feedback (gestionnaire) */}
      <Modal isOpen={isReviewFeedbackModalOpen} onClose={() => {
      setIsReviewFeedbackModalOpen(false);
      setReviewingFeedback(null);
      setManagerComment('');
    }} title="Examiner le feedback" size="lg" data-id="element-1267">
        {reviewingFeedback && <div className="space-y-6" data-id="element-1268">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1269">
              <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-1270">
                <div data-id="element-1271">
                  <span className="text-xs text-gray-500" data-id="element-1272">De</span>
                  <p className="font-medium text-gray-900" data-id="element-1273">
                    {reviewingFeedback.from}
                  </p>
                </div>
                <div data-id="element-1274">
                  <span className="text-xs text-gray-500" data-id="element-1275">À</span>
                  <p className="font-medium text-gray-900" data-id="element-1276">
                    {reviewingFeedback.to}
                  </p>
                </div>
              </div>
              <div className="mb-3" data-id="element-1277">
                <span className="text-xs text-gray-500" data-id="element-1278">Type</span>
                <p data-id="element-1279">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${reviewingFeedback.type === 'positive' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1280">
                    {reviewingFeedback.type === 'positive' ? 'Positif' : 'Constructif'}
                  </span>
                </p>
              </div>
              <div data-id="element-1281">
                <span className="text-xs text-gray-500" data-id="element-1282">Message</span>
                <p className="text-sm text-gray-900 mt-1 leading-relaxed" data-id="element-1283">
                  {reviewingFeedback.message}
                </p>
              </div>
            </div>

            <div data-id="element-1284">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1285">
                Commentaire du gestionnaire (optionnel)
              </label>
              <textarea value={managerComment} onChange={e => setManagerComment(e.target.value)} placeholder="Ajoutez un commentaire ou des suggestions..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-1286" />
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-1287">
              <button onClick={handleRejectFeedback} className="w-full sm:w-auto px-6 py-2.5 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2" data-id="element-1288">
                <X className="h-4 w-4" data-id="element-1289" />
                Rejeter
              </button>
              <button onClick={handleApproveFeedback} className="w-full sm:w-auto px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-1290">
                <Check className="h-4 w-4" data-id="element-1291" />
                Approuver et envoyer
              </button>
            </div>
          </div>}
      </Modal>

      {/* Modal pour modifier l'ordre du jour - VERSION AMÉLIORÉE */}
      <Modal isOpen={isAgendaModalOpen} onClose={() => setIsAgendaModalOpen(false)} title="Modifier l'ordre du jour" size="xl" data-id="element-1292">
        <div className="space-y-6" data-id="element-1293">
          {/* En-tête avec durée totale */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-1294">
            <div data-id="element-1295">
              <p className="text-sm font-medium text-blue-900" data-id="element-1296">
                Durée totale de la réunion
              </p>
              <p className="text-xs text-blue-700 mt-1" data-id="element-1297">
                Planifiez efficacement votre temps
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-1298">
              <Clock className="h-5 w-5 text-blue-600" data-id="element-1299" />
              <span className="text-2xl font-bold text-blue-900" data-id="element-1300">
                {getTotalDuration()}
              </span>
              <span className="text-sm text-blue-700" data-id="element-1301">minutes</span>
            </div>
          </div>

          <p className="text-sm text-gray-600" data-id="element-1302">
            Glissez-déposez les éléments pour réorganiser l'ordre du jour. Vous
            pouvez aussi ajouter, modifier ou supprimer des éléments.
          </p>

          {/* Liste des éléments de l'agenda */}
          <div className="space-y-3" data-id="element-1303">
            {tempAgenda.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1304">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" data-id="element-1305" />
                <p className="text-sm" data-id="element-1306">Aucun élément dans l'ordre du jour</p>
                <p className="text-xs mt-1" data-id="element-1307">
                  Ajoutez votre premier élément ci-dessous
                </p>
              </div> : tempAgenda.map((item, index) => <div key={item.id} draggable onDragStart={() => handleDragStart(item.id)} onDragOver={e => handleDragOver(e, item.id)} onDragEnd={handleDragEnd} className="flex items-start gap-3 p-4 bg-white border-2 border-gray-200 rounded-lg cursor-move hover:border-blue-300 hover:shadow-md transition-all" data-id="element-1308">
                  <div className="flex flex-col items-center gap-1 pt-1" data-id="element-1309">
                    <GripVertical className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-1310" />
                    <span className="text-xs font-medium text-gray-500" data-id="element-1311">
                      #{index + 1}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1312">
                    {editingItemId === item.id ? <div className="space-y-2" data-id="element-1313">
                        <input type="text" value={editingItemText} onChange={e => setEditingItemText(e.target.value)} className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus data-id="element-1314" />
                        <div className="flex gap-2" data-id="element-1315">
                          <button onClick={() => handleSaveEditItem(item.id)} className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1" data-id="element-1316">
                            <CheckCircle className="h-3 w-3" data-id="element-1317" />
                            Sauvegarder
                          </button>
                          <button onClick={handleCancelEditItem} className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300" data-id="element-1318">
                            Annuler
                          </button>
                        </div>
                      </div> : <>
                        <div className="flex items-start justify-between gap-2 mb-2" data-id="element-1319">
                          <p className="text-sm font-medium text-gray-900 flex-1" data-id="element-1320">
                            {item.text}
                          </p>
                          <button onClick={() => handleToggleStatus(item.id)} className="flex-shrink-0" title={item.status === 'completed' ? 'Marquer comme non fait' : 'Marquer comme fait'} data-id="element-1321">
                            {item.status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600" data-id="element-1322" /> : <Circle className="h-5 w-5 text-gray-300 hover:text-gray-400" data-id="element-1323" />}
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600" data-id="element-1324">
                          <div className="flex items-center gap-1" data-id="element-1325">
                            <Clock className="h-3 w-3" data-id="element-1326" />
                            <select value={item.duration} onChange={e => handleUpdateDuration(item.id, parseInt(e.target.value))} className="border-0 bg-transparent p-0 focus:ring-0 text-xs cursor-pointer hover:text-blue-600" data-id="element-1327">
                              <option value={5} data-id="element-1328">5 min</option>
                              <option value={10} data-id="element-1329">10 min</option>
                              <option value={15} data-id="element-1330">15 min</option>
                              <option value={20} data-id="element-1331">20 min</option>
                              <option value={25} data-id="element-1332">25 min</option>
                              <option value={30} data-id="element-1333">30 min</option>
                              <option value={45} data-id="element-1334">45 min</option>
                              <option value={60} data-id="element-1335">60 min</option>
                            </select>
                          </div>
                          <span className="text-gray-400" data-id="element-1336">•</span>
                          <span className="flex items-center gap-1" data-id="element-1337">
                            <Users className="h-3 w-3" data-id="element-1338" />
                            {item.owner}
                          </span>
                          {item.status === 'completed' && <>
                              <span className="text-gray-400" data-id="element-1339">•</span>
                              <span className="text-green-600 font-medium" data-id="element-1340">
                                Complété
                              </span>
                            </>}
                        </div>
                      </>}
                  </div>

                  <div className="flex gap-1" data-id="element-1341">
                    {editingItemId !== item.id && <button onClick={() => handleStartEditItem(item)} className="p-2 hover:bg-blue-100 rounded transition-colors" title="Modifier" data-id="element-1342">
                        <Edit2 className="h-4 w-4 text-blue-600" data-id="element-1343" />
                      </button>}
                    <button onClick={() => handleRemoveAgendaItem(item.id)} className="p-2 hover:bg-red-100 rounded transition-colors" title="Supprimer" data-id="element-1344">
                      <X className="h-4 w-4 text-red-600" data-id="element-1345" />
                    </button>
                  </div>
                </div>)}
          </div>

          {/* Ajouter un nouvel élément */}
          <div className="border-t pt-6" data-id="element-1346">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1347">
              <Plus className="h-4 w-4" data-id="element-1348" />
              Ajouter un élément à l'ordre du jour
            </h3>
            <div className="space-y-3" data-id="element-1349">
              <div data-id="element-1350">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1351">
                  Sujet à discuter
                </label>
                <input type="text" value={newAgendaItem} onChange={e => setNewAgendaItem(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddAgendaItem()} placeholder="Ex: Discussion sur les objectifs Q1" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1352" />
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-1353">
                <div data-id="element-1354">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1355">
                    Durée (minutes)
                  </label>
                  <select value={newAgendaDuration} onChange={e => setNewAgendaDuration(parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1356">
                    <option value={5} data-id="element-1357">5 minutes</option>
                    <option value={10} data-id="element-1358">10 minutes</option>
                    <option value={15} data-id="element-1359">15 minutes</option>
                    <option value={20} data-id="element-1360">20 minutes</option>
                    <option value={25} data-id="element-1361">25 minutes</option>
                    <option value={30} data-id="element-1362">30 minutes</option>
                    <option value={45} data-id="element-1363">45 minutes</option>
                    <option value={60} data-id="element-1364">60 minutes</option>
                  </select>
                </div>
                <div data-id="element-1365">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1366">
                    Responsable
                  </label>
                  <input type="text" value={newAgendaOwner} onChange={e => setNewAgendaOwner(e.target.value)} placeholder={isManager ? 'Manager' : userName} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1367" />
                </div>
              </div>
              <button onClick={handleAddAgendaItem} disabled={!newAgendaItem.trim()} className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors" data-id="element-1368">
                <Plus className="h-4 w-4" data-id="element-1369" />
                Ajouter à l'ordre du jour
              </button>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-1370">
            <button onClick={() => setIsAgendaModalOpen(false)} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1371">
              Annuler
            </button>
            <button onClick={handleSaveAgenda} className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-1372">
              <Check className="h-4 w-4" data-id="element-1373" />
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal pour les commentaires anonymes */}
      <Modal isOpen={isCommentsModalOpen} onClose={() => {
      setIsCommentsModalOpen(false);
      setViewingCommentsOneOnOneId(null);
      setNewAnonymousComment('');
    }} title="Commentaires anonymes" size="lg" data-id="element-1374">
        <div className="space-y-6" data-id="element-1375">
          {/* En-tête explicatif */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-1376">
            <div className="flex items-start gap-3" data-id="element-1377">
              <Lock className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-1378" />
              <div data-id="element-1379">
                <h3 className="font-semibold text-purple-900 mb-1" data-id="element-1380">
                  Espace confidentiel et anonyme
                </h3>
                <p className="text-sm text-purple-700 leading-relaxed" data-id="element-1381">
                  Partagez vos préoccupations, suggestions ou sujets sensibles
                  de manière totalement anonyme. Les commentaires ne sont pas
                  attribués à une personne spécifique.
                </p>
              </div>
            </div>
          </div>

          {/* Liste des commentaires */}
          <div className="space-y-3" data-id="element-1382">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1383">
              Commentaires partagés ({getCurrentOneOnOneComments().length})
            </h3>

            {getCurrentOneOnOneComments().length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1384">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-400" data-id="element-1385" />
                <p className="text-sm" data-id="element-1386">Aucun commentaire pour le moment</p>
                <p className="text-xs mt-1" data-id="element-1387">
                  Soyez le premier à partager vos réflexions
                </p>
              </div> : getCurrentOneOnOneComments().map(comment => <div key={comment.id} className={`p-4 rounded-lg border-2 ${comment.isRead ? 'border-gray-200 bg-gray-50' : 'border-purple-200 bg-purple-50'}`} data-id="element-1388">
                  <div className="flex items-start justify-between gap-3 mb-2" data-id="element-1389">
                    <div className="flex items-center gap-2" data-id="element-1390">
                      <Lock className="h-4 w-4 text-gray-400" data-id="element-1391" />
                      <span className="text-xs text-gray-500" data-id="element-1392">Anonyme</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1393">
                      <span className="text-xs text-gray-500" data-id="element-1394">
                        {comment.timestamp}
                      </span>
                      {!comment.isRead && <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium" data-id="element-1395">
                          Nouveau
                        </span>}
                    </div>
                  </div>
                  <p className="text-sm text-gray-900 mb-3" data-id="element-1396">{comment.text}</p>
                  {!comment.isRead && isManager && <button onClick={() => handleMarkCommentAsRead(comment.id)} className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1" data-id="element-1397">
                      <Check className="h-3 w-3" data-id="element-1398" />
                      Marquer comme lu
                    </button>}
                </div>)}
          </div>

          {/* Ajouter un nouveau commentaire */}
          <div className="border-t pt-6" data-id="element-1399">
            <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1400">
              Ajouter un commentaire anonyme
            </h3>
            <div className="space-y-3" data-id="element-1401">
              <textarea value={newAnonymousComment} onChange={e => setNewAnonymousComment(e.target.value)} placeholder="Partagez vos réflexions, préoccupations ou suggestions de manière anonyme..." rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none" data-id="element-1402" />
              <button onClick={handleAddAnonymousComment} disabled={!newAnonymousComment.trim()} className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors" data-id="element-1403">
                <Send className="h-4 w-4" data-id="element-1404" />
                Envoyer anonymement
              </button>
            </div>
          </div>

          {/* Bouton de fermeture */}
          <div className="flex justify-end pt-4 border-t" data-id="element-1405">
            <button onClick={() => {
            setIsCommentsModalOpen(false);
            setViewingCommentsOneOnOneId(null);
            setNewAnonymousComment('');
          }} className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1406">
              Fermer
            </button>
          </div>
        </div>
      </Modal>
    </div>;
}